import type { AssignmentDelay } from './ApiAssignment';
import type { AssignedDepot } from './ApiDepot';
import type { TaskTypes } from './ApiTask';
import type { VehicleType } from './ApiVehicle';
import type {
    Contact,
    Coordinates,
    FullAddressWithLocation,
    TimeWindow
} from './CommonTypes';
import type { LiveBreakDetails } from './DriverBreak';

export enum DepotStopNumbers {
    START_DEPOT = 'start',
    END_DEPOT = 'end'
}

export interface SocketScheduleItemDriverProfile {
    /**
     * The driver first name
     */
    firstname: string;

    /**
     * The driver last name
     */
    lastname: string;

    /**
     * The driver ID
     */
    id: string;
}

export interface SocketScheduleItemProps {
    inventory?: Record<string, unknown>[];
    invoices?: Record<string, unknown>[];
    priority: number;
    serviceTime: string;
    size: number;
    time?: string;
    weight: number;
    height?: number;
    window: TimeWindow[];
    rawTimeWindow: TimeWindow[];
}

export type SocketScheduleItem = {
    abbreviatedType?: string;
    arrivalTime: string;
    breakDetails?: LiveBreakDetails;
    client?: string;
    completedAt: string;
    contact?: Partial<Contact>;
    delay: AssignmentDelay;
    multiTimeWindowDelay?: AssignmentDelay;
    depotIcon: string;
    depotId?: string;
    driver?: string;
    driverProfile: SocketScheduleItemDriverProfile;
    eorder?: string;
    eta: string;
    rawEta: string;
    euid?: string;
    hasInventoryException: boolean;
    iconTextColor?: string;
    id: string;
    index?: number;
    inventoryItems: Record<string, unknown>[];
    isActive?: boolean;
    isActiveAssignment?: boolean;
    isCancelled?: boolean;
    isCompleted: boolean;
    isDepot: boolean;
    isInProgress?: boolean;
    isPaired?: boolean;
    lastStopInShift?: boolean;
    listPosition?: string;
    location: FullAddressWithLocation;
    name: string;
    onTime?: boolean;
    owner?: string;
    progressBar?: string;
    props: SocketScheduleItemProps;
    serviceTime: string;
    serviceTimeML: boolean;
    size: number;
    startServiceAt: string;
    status: number;
    statusBackground?: string;
    statusBorder?: string;
    statusDisplay?: string;
    statusPin?: string;
    statusText?: string;
    statusTextColor?: string;
    stopname: string;
    stopnumber: DepotStopNumbers | number;
    subRouteId?: string;
    task: string;
    taskLabels: string[];
    taskName?: string;
    textColor?: string;
    timeWindow: TimeWindow[];
    type: TaskTypes;
    typeIcon?: string;
};

export interface SocketDriverProfile {
    firstname?: string;
    initials: string;
    lastname?: string;
    name?: string;
    phone?: string;
}

export interface SocketDriverProps {
    formattedShiftTimes?: TimeWindow[];
    shiftExceptions?: Record<string, unknown>;
    shiftTime?: TimeWindow[];
    shiftTimes?: {
        [day: string]: {
            start?: string;
            duration?: string;
        }[];
    };
}

export interface SocketDriverStats {
    activeAssignmentIndex: number;
    atRiskWatch: number;
    cancelled?: number;
    cardHeight?: string;
    casesDelivered?: number;
    casesPercentage?: number;
    casesUnDelivered?: number;
    complete?: number;
    depot?: number;
    driverHasActiveLocation: boolean;
    driverHasActiveSchedule?: boolean;
    isDriverComplete: boolean;
    largestSchedule?: number;
    lateWatch: number;
    locationHoverMessage?: string;
    locationMessage?: string;
    mapIcon?: string;
    numConfirmedInventoryItems: number;
    numInventoryExceptions: number;
    numInventoryItems: number;
    onTime?: number;
    percentage?: number;
    progressBarLength?: number;
    remaining?: number;
    routeIdMessage?: string;
    timeRemaining: {
        hours?: number;
        minutes?: number;
        ms: number;
    };
    totalSch: number;
    totalUnits: number;
    totalWeight: number;
}

export interface SocketDriverVehicleCompartmentItem {
    /**
     * The compartment capacity
     */
    capacity: number;

    /**
     * The compartment type ID
     */
    compartmentType: string;
}

export interface SocketDriverVehicle {
    available?: boolean;
    compartments?: SocketDriverVehicleCompartmentItem[];
    description?: string;
    eid?: string;
    enableRta?: boolean;
    endDepot?: string;
    id: string;
    licensePlate?: string;
    makemodel?: string;
    startDepot?: string;
    type: VehicleType;
    volumeCapacity: number;
    weightCapacity: number;
    year?: string;
}

export interface SocketDriver {
    activated?: boolean;
    client: string;
    defaultVehicle?: string;
    /**
     * The driver home depot ID
     *
     * When the driver has a different `startDepot` and `endDepot`, this value will match the `endDepot` ID
     *
     * While the driver is not yet assigned to a route, this value will be an empty string
     */
    depotId?: string;
    /**
     * The driver start depot data
     *
     * While the driver is not yet assigned to a route, this will be an `empty object`
     */
    startDepot: AssignedDepot;
    /**
     * The driver end depot data
     *
     * While the driver is not yet assigned to a route, this will be an `empty object`
     */
    endDepot: AssignedDepot;
    deviceType?: string;
    driver?: string;
    eid: string;
    endingShift?: boolean;
    firstname?: string;
    id: string;
    lastLocationUpdate: string;
    lastname?: string;
    location: Coordinates;
    profile: SocketDriverProfile;
    props?: SocketDriverProps;
    schedule: SocketScheduleItem[];
    stats: SocketDriverStats;
    telephone?: string;
    vehicle: SocketDriverVehicle;
}
